import {
  Component,
  OnInit,
  Injector,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import {
  Router,
  NavigationEnd,
  ActivatedRoute,
  ActivationEnd,
} from '@angular/router';
import { UserProfile } from '../../auth/profile/profile.service';
import { MenuService } from '../../auth/menu/menu.service';
import { SettingsService } from '../../core/settings/settings.service';
import { BotService } from '../../chatstyle/bot/bot.service';
import { MenuItem } from '../../auth/menu-items';

declare var $: any;

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnInit, OnDestroy {
  menuItems: MenuItem[] = [];
  router: Router;
  sbclickEvent = 'click.sidebar-toggle';
  $doc: any = null;

  public botID: string | null = null;
  public botState: string | null = null;
  public botConfigurationType: string | null = null;

  profile: UserProfile;
  checked = false;
  singleLevel = true;

  constructor(
    public menu: MenuService,
    public settings: SettingsService,
    public injector: Injector,
    private actRoute: ActivatedRoute,
    private botService: BotService,
    public initialRouter: Router,
    private cdr: ChangeDetectorRef
  ) {
    // Set initial sidebar width CSS variable on load
    this.setSidebarWidthCSSVar(this.settings.getLayoutSetting('isCollapsed'));
    if (this.initialRouter && this.initialRouter.url) {
      this.checkBot(this.initialRouter.url);
    }
  }

  private async checkMenuAsync(): Promise<boolean> {
    if (this.checked) {
      return Promise.resolve(this.checked);
    }

    return new Promise((resolve, reject) => {
      this.botService.getBots().subscribe({
        next: (result) => {
          this.checked = true;

          if (result.length === 1) {
            this.singleLevel = true;
            this.botID = result[0].botID;
            this.botState = result[0].state;
            this.botConfigurationType = result[0].configurationType;
          } else {
            this.singleLevel = false;
            this.botID = null;
            this.botState = null;
            this.botConfigurationType = null;
          }
          resolve(this.checked);
        },
        error: (err) => {
          console.error(err);
          reject(err);
        }
      });
    });
  }

  ngOnInit(): void {
    this.router = this.injector.get(Router);

    // Listen for sidebar collapse/expand changes
    this.settings.layoutSettingsChange?.subscribe((change: any) => {
      if (change && change.key === 'isCollapsed') {
        this.setSidebarWidthCSSVar(change.value);
      }
    });

    this.router.events.subscribe((val) => {
      if (val instanceof ActivationEnd) {
        if (!this.singleLevel) {
          this.botID = val.snapshot.params.id || null;
        }
      }

      if (val instanceof NavigationEnd) {
        this.checkBot(val.url);

        // Close any submenu opened when route changes
        this.removeFloatingNav();
        // Scroll view to top
        window.scrollTo(0, 0);
        // Close sidebar on route change
        this.settings.setLayoutSetting('asideToggled', false);
      }
    });

    // Enable sidebar autoclose from external clicks
    this.anyClickClose();
  }

  load(profile: UserProfile): void {
    this.profile = profile;
  }

  private checkBot(url: string): void {
    this.checkMenuAsync()
      .then(() => {
        if (this.singleLevel) {
          this.updateMenuItems(url);
        } else {
          if (url) {
            const parts: string[] = url.split('/');
            if (parts && parts.length > 2 && parts[1] === 'bots') {
              this.botID = parts[2];

              this.botService.getBot(this.botID).then(
                (result) => {
                  this.botState = result.state;
                  this.botConfigurationType = result.configurationType;
                  this.updateMenuItems(url);
                },
                (err) => {
                  console.error('Error fetching bot:', err);
                  this.updateMenuItems(url);
                }
              );
            } else {
              this.updateMenuItems(url);
            }
          } else {
            this.updateMenuItems('');
          }
        }
      })
      .catch((err) => {
        console.error('Error checking menu:', err);
      });
  }

  private updateMenuItems(url: string): void {
    const newMenuItems = this.getMenuItems(url);

    // Only update if the menu items actually changed to prevent unnecessary re-renders
    if (
      !this.menuItems ||
      this.menuItemsChanged(this.menuItems, newMenuItems)
    ) {
      this.menuItems = newMenuItems;
      this.cdr.markForCheck(); // Only trigger change detection when menu actually changes
    }
  }

  private menuItemsChanged(
    oldItems: MenuItem[],
    newItems: MenuItem[]
  ): boolean {
    if (!oldItems && !newItems) return false;
    if (!oldItems || !newItems) return true;
    if (oldItems.length !== newItems.length) return true;

    // Compare key properties to detect meaningful changes
    for (let i = 0; i < oldItems.length; i++) {
      if (
        oldItems[i].text !== newItems[i].text ||
        oldItems[i].link !== newItems[i].link ||
        oldItems[i].icon !== newItems[i].icon
      ) {
        return true;
      }
    }
    return false;
  }

  trackByMenuItem(index: number, item: MenuItem): string {
    return item.text + (item.link || '') + (item.icon || '');
  }

  private getMenuItems(url: string): MenuItem[] {
    if (this.menu.hasPrivilege('User') && this.botID && this.singleLevel) {
      return this.menu.getCustomerMenu(
        this.botID,
        this.botState,
        this.botConfigurationType
      );
    }
    if (url && (url.startsWith('/bots/') || url.startsWith('/organization/'))) {
      return this.menu.getBotMenu(
        this.botID,
        this.botState,
        this.botConfigurationType
      );
    }
    return this.menu.getMenu();
  }

  private anyClickClose(): void {
    this.$doc = $(document).on(this.sbclickEvent, (e) => {
      if (!$(e.target).parents('.aside-container').length) {
        this.settings.setLayoutSetting('asideToggled', false);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.$doc) {
      this.$doc.off(this.sbclickEvent);
    }
  }

  toggleSubmenuClick(event): void {
    event.preventDefault();

    if (
      !this.isSidebarCollapsed() &&
      !this.isSidebarCollapsedText() &&
      !this.isEnabledHover()
    ) {
      let ul = $(event.currentTarget.nextElementSibling);

      // hide other submenus
      let parentNav = ul.parents('.sidebar-subnav');
      $('.sidebar-subnav').each((idx, el) => {
        let $el = $(el);
        // if element is not a parent or self ul
        if (el !== parentNav[0] && el !== ul[0]) {
          this.closeMenu($el);
        }
      });

      // abort if not UL to process
      if (!ul.length) {
        return;
      }

      // any child menu should start closed
      ul.find('.sidebar-subnav').each((idx, el) => {
        this.closeMenu($(el));
      });

      // toggle UL height
      const ulHeight = ul.css('height');
      if (ulHeight === 'auto' || parseInt(ulHeight, 10)) {
        this.closeMenu(ul);
      } else {
        // expand menu
        ul.on('transitionend', () => {
          ul.css('height', 'auto').off('transitionend');
        }).css('height', ul[0].scrollHeight);
        // add class to manage animation
        ul.addClass('opening');
      }
    }
  }

  private closeMenu(elem: any): void {
    elem.css('height', elem[0].scrollHeight);
    elem.css('height', 0);
    elem.removeClass('opening');
  }

  toggleSubmenuHover(event): void {
    let self = this;
    if (
      this.isSidebarCollapsed() ||
      this.isSidebarCollapsedText() ||
      this.isEnabledHover()
    ) {
      event.preventDefault();

      this.removeFloatingNav();

      let ul = $(event.currentTarget.nextElementSibling);
      let anchor = $(event.currentTarget);

      if (!ul.length) {
        return; // if not submenu return
      }

      let $aside = $('.aside-container');
      let $asideInner = $aside.children('.aside-inner'); // for top offset calculation
      let $sidebar = $asideInner.children('.sidebar');
      let mar =
        parseInt($asideInner.css('padding-top'), 0) +
        parseInt($aside.css('padding-top'), 0);
      let itemTop = anchor.parent().position().top + mar - $sidebar.scrollTop();

      let floatingNav = ul.clone().appendTo($aside);
      let vwHeight = document.body.clientHeight;

      // let itemTop = anchor.position().top || anchor.offset().top;

      floatingNav.addClass('nav-floating');

      // each item has ~40px height
      // multiply to force space for at least N items
      var safeOffsetValue = 40 * 5;
      var navHeight = floatingNav.outerHeight(true) + 2; // 2px border
      var safeOffset =
        navHeight < safeOffsetValue ? navHeight : safeOffsetValue;

      var displacement = 25; // displacement in px from bottom

      // if not enough space to show N items, use then calculated 'safeOffset'
      var menuTop =
        vwHeight - itemTop > safeOffset
          ? itemTop
          : vwHeight - safeOffset - displacement;

      floatingNav
        .removeClass('opening') // necesary for demo if switched between normal//collapsed mode
        .css({
          position: this.settings.getLayoutSetting('isFixed')
            ? 'fixed'
            : 'absolute',
          top: menuTop,
          bottom:
            floatingNav.outerHeight(true) + menuTop > vwHeight
              ? displacement + 'px'
              : 'auto',
        });

      floatingNav
        .on('mouseleave', () => {
          floatingNav.remove();
        })
        .find('a')
        .on('click', function (e) {
          e.preventDefault(); // prevents page reload on click
          // get the exact route path to navigate
          let routeTo = $(this).attr('route');
          if (routeTo) self.router.navigate([routeTo]);
        });

      this.listenForExternalClicks();
    }
  }

  private listenForExternalClicks(): void {
    const $doc = $(document).on('click.sidebar', (e) => {
      if (!$(e.target).parents('.aside-container').length) {
        this.removeFloatingNav();
        $doc.off('click.sidebar');
      }
    });
  }

  private removeFloatingNav(): void {
    $('.nav-floating').remove();
  }

  private setSidebarWidthCSSVar(isCollapsed: boolean): void {
    const width = isCollapsed ? '60px' : '220px';
    document.documentElement.style.setProperty('--sidebar-width', width);
  }

  private isSidebarCollapsed(): boolean {
    return this.settings.getLayoutSetting('isCollapsed');
  }

  private isSidebarCollapsedText(): boolean {
    return this.settings.getLayoutSetting('isCollapsedText');
  }

  private isEnabledHover(): boolean {
    return this.settings.getLayoutSetting('asideHover');
  }

  toggleCollapsedSideabar(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.settings.toggleLayoutSetting('isCollapsed');
  }
}
